import React from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import { Check, X } from 'react-feather'
import { useTranslation } from 'react-i18next'
import useToast from 'hooks/useToast'
import {
  useCastClanVoteMutation,
  useGetMyClanVoteQuery
} from 'state/services/community/community'
import { useSession } from 'views/Community/components/useSession'

const Wrapper = styled(Flex)`
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
`

const Choices = styled(Flex)`
  gap: 8px;
  flex-wrap: wrap;
`

const Choice = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray8)};
  background: ${({ theme, $active }) => ($active ? theme.colors.cyan : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.background : theme.colors.white)};
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const Verdict = styled(Flex)`
  gap: 8px;
  align-items: center;
`

const CHOICES: ('yes' | 'no' | 'abstain')[] = ['yes', 'no', 'abstain']

/**
 * The ballot, for anybody who is in a clan.
 *
 * Not a second vote alongside the personal one — the only one. A member of a
 * clan votes here, the clan decides, and the clan carries the voting power of
 * its whole membership at double weight to the DAO. Showing both this and the
 * personal buttons is what made the flow unreadable: two ballots on one screen,
 * one of which the server would refuse.
 *
 * Renders nothing for somebody in no clan. Everything that needs explaining is
 * behind the "i".
 */
const ClanVoteCard: React.FC<{ proposalId: string }> = ({ proposalId }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { toastError } = useToast()
  const { account, accessToken, ready } = useSession()
  const session = { account: account as string, accessToken: accessToken as string }

  const { data } = useGetMyClanVoteQuery(
    { ...session, proposalId },
    // Every other member votes in their own browser, so this cannot wait for a
    // local action to invalidate it.
    { skip: !ready || !proposalId, refetchOnMountOrArgChange: true, pollingInterval: 20_000 }
  )
  const [cast, { isLoading }] = useCastClanVoteMutation()

  if (!ready || !data?.clan) return null

  const clan = data.clan
  const votes = clan.votesCast ?? clan.votedWallets.length
  const counts = clan.counts ?? { yes: 0, no: 0, abstain: 0 }
  const decided = clan.status === 'decided' && clan.choice

  const onCast = async (choice: 'yes' | 'no' | 'abstain') => {
    try {
      await cast({ ...session, proposalId, choice }).unwrap()
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message
      toastError(t('dao.clanVoteFailed'), message ?? t('general.tryAgain'))
    }
  }

  return (
    <Wrapper>
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="8px">
        <Text variant={TextVariant.BODY_DEFAULT_BOLD}>
          {t('dao.clanBallot')}
          <InfoIcon
            iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
            tooltipProps={{ title: t('dao.clanBallotTooltip') }}
          />
        </Text>
        <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
          {t('dao.clanSplit', {
            yes: counts.yes,
            no: counts.no,
            abstain: counts.abstain
          })}
        </Text>
      </Flex>

      {/*
        The choice you have already cast is not pressable.
        
        Leaving it live so that "you can change your mind" reads as a ballot
        that never registered — which is exactly the report this comes from,
        twice. Casting the same vote again is not an action, so the button for
        it is spent; the other two stay live because changing your mind while
        the proposal is open is legitimate, and the label says that is what
        they now do.
      */}
      <Choices>
        {CHOICES.map((c) => {
          const mine = data.myChoice === c
          return (
            <Choice
              key={c}
              $active={mine}
              disabled={isLoading || mine}
              onClick={() => void onCast(c)}
            >
              {/* react-feather, like every other mark in the portal — a bare
                  glyph renders in whatever the operating system feels like. */}
              {t(`dao.choice.${c}`)}
              {mine ? <Check size={13} style={{ marginLeft: 6 }} /> : null}
            </Choice>
          )
        })}
      </Choices>

      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {data.myChoice
          ? t('dao.clanChangeVote', { choice: t(`dao.choice.${data.myChoice}`) })
          : t('dao.clanNotVotedYet')}
      </Text>

      {/*
        One line saying which of the three states the clan is in, because that
        is the only question a member has when they look at this.
      */}
      {decided ? (
        <Verdict>
          <Check size={15} />
          <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.cyan}>
            {t('dao.clanDecided', { choice: t(`dao.choice.${clan.choice}`) })}
          </Text>
        </Verdict>
      ) : clan.status === 'tied' ? (
        <Verdict>
          <X size={15} />
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            {t('dao.clanTied', {
              votes,
              members: clan.memberCount,
              yes: counts.yes,
              no: counts.no
            })}
          </Text>
        </Verdict>
      ) : (
        <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
          {t('dao.clanNeedsMore', { votes, members: clan.memberCount })}
        </Text>
      )}

      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('dao.clanCastsPower', { members: clan.memberCount })}
        {(data.awaiting?.length ?? 0) > 0
          ? ` · ${t('dao.clanAwaitingMembers', { count: data.awaiting?.length ?? 0 })}`
          : ''}
      </Text>
    </Wrapper>
  )
}

export default ClanVoteCard
